import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";

export default function AuditLogPage() {
  const { lang } = useLang();
  const [rows, setRows] = useState([]);

  const load = () =>
    api.get("/audit?limit=200").then((r) => setRows(r.data.logs || [])).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const rollback = async (proposalId) => {
    if (!window.confirm(`Rollback proposal ${proposalId}?`)) return;
    try {
      await api.post(`/learning/proposals/${proposalId}/rollback`);
      toast.success("Rolled back");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="audit-page">
      <div>
        <h1 className="font-display font-light text-3xl tracking-tight">
          {t(lang, "audit_title")}
        </h1>
        <p className="mt-1 text-white/50 text-sm">{t(lang, "audit_subtitle")}</p>
      </div>

      <div className="hq-card overflow-hidden">
        <table className="hq-table">
          <thead>
            <tr>
              <th>{t(lang, "audit_ts")}</th>
              <th>{t(lang, "audit_action")}</th>
              <th>{t(lang, "audit_actor")}</th>
              <th>{t(lang, "audit_meta")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const isApplied = r.action === "learning.proposal.applied";
              const proposalId = r?.meta?.proposal_id;
              return (
                <tr key={r.log_id} data-testid={`audit-row-${r.log_id}`}>
                  <td className="font-mono text-xs text-white/70 whitespace-nowrap">
                    {formatTs(r.created_at)}
                  </td>
                  <td className="font-mono text-xs">{r.action}</td>
                  <td className="font-mono text-xs text-white/70">
                    {r.actor_user_id || "—"}
                  </td>
                  <td className="text-xs text-white/60 max-w-md truncate">
                    {formatMeta(r.meta)}
                  </td>
                  <td className="whitespace-nowrap">
                    {isApplied && proposalId ? (
                      <button
                        onClick={() => rollback(proposalId)}
                        className="hq-btn hq-btn-ghost text-xs"
                        data-testid={`audit-rollback-${proposalId}`}
                      >
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Rollback
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-white/40 py-8 font-mono text-xs">
                  no events
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toISOString().replace("T", " ").slice(0, 19);
  } catch {
    return ts;
  }
}

function formatMeta(m) {
  if (!m) return "—";
  try {
    return JSON.stringify(m);
  } catch {
    return String(m);
  }
}
